import { API_URL } from "consts";
import NotFoundImg from "assets/images/not-found.png";
import { PClassEavTargetValuesEnum, PClassEminTargetValuesEnum, CClassEavTargetValuesEnum, CClassUoTargetValuesEnum } from "types";
import type { IFamilyConfig, ILightingClassConfig, PClassEnum, CClassEnum, PCustomClassData, CCustomClassData } from "types";
import type { IResultItem } from "../types";
import type { CPClassSymbol } from "./consts";

export const filterValidOptions = (options: IResultItem[]): IResultItem[] =>
  options.filter((e) => e.RoadStandard !== "notmet" && e.RoadStandard !== "toohigh" && e.RoadStandard !== "toohight");

export const getFamilyImage = (familyId: string, families: any): string => {
  if ("data" in families && families.data?.families) {
    const family = families.data.families.find((obj: { id: string; image?: string }) => obj.id === familyId);
    if (family?.image) return API_URL + family.image;
  }
  return NotFoundImg;
};

export const getFamilyDescription = (families: any, familyConfig: IFamilyConfig): string => {
  if (!("data" in families)) return "";
  const family = families.data.families.find((obj: any) => familyConfig.familyIds.includes(obj.id));
  return family?.description ?? "";
};

export type ClassTypeFlags = {
  isRoadClassM: boolean;
  isRoadClassC: boolean;
  isRoadClassP: boolean;
  isLeftBorderClassP: boolean;
  isLeftBorderClassC: boolean;
  isRightBorderClassP: boolean;
  isRightBorderClassC: boolean;
};

type BorderClassParams = {
  borderClass: PClassEnum | CClassEnum | undefined;
  isBorderClassP: boolean;
  isBorderClassC: boolean;
  pCustomBorderClass: PCustomClassData | undefined;
  cCustomBorderClass: CCustomClassData | undefined;
};

export const getBorderClassValue = (symbol: CPClassSymbol, params: BorderClassParams): string | number | undefined => {
  const { borderClass, isBorderClassP, isBorderClassC, pCustomBorderClass, cCustomBorderClass } = params;
  const pClassEav = borderClass as keyof typeof PClassEavTargetValuesEnum;
  const pClassEmin = borderClass as keyof typeof PClassEminTargetValuesEnum;
  const cClassEav = borderClass as keyof typeof CClassEavTargetValuesEnum;
  const cClassUo = borderClass as keyof typeof CClassUoTargetValuesEnum;

  if (isBorderClassP) {
    if (symbol === "e_avg") return pCustomBorderClass ? pCustomBorderClass.Em_av : PClassEavTargetValuesEnum[pClassEav];
    if (symbol === "e_min") return pCustomBorderClass ? pCustomBorderClass.Em_min : PClassEminTargetValuesEnum[pClassEmin];
    return undefined;
  }

  if (isBorderClassC) {
    if (symbol === "e_avg") return cCustomBorderClass ? cCustomBorderClass.Em_av : CClassEavTargetValuesEnum[cClassEav];
    if (symbol === "u_oe") return cCustomBorderClass ? cCustomBorderClass.E_uo : CClassUoTargetValuesEnum[cClassUo];
    return undefined;
  }

  return undefined;
};

export const getBorderClassUnit = (symbol: CPClassSymbol, params: BorderClassParams): string => {
  const value = getBorderClassValue(symbol, params);
  if (symbol === "u_oe") return value ? `${value}` : "";
  return value ? `${value} (lx)` : "(lx)";
};

export const getClassTypeFlags = (lightingClassConfig: ILightingClassConfig): ClassTypeFlags => {
  const roadClasses = lightingClassConfig?.roadClasses;
  const leftBorderClass = lightingClassConfig?.borderAreaLeftClasses;
  const rightBorderClass = lightingClassConfig?.borderAreaRightClasses;
  const customRoadClass = lightingClassConfig?.customClasses?.mainCustomClass;
  const customLeftBorderClass = lightingClassConfig?.customClasses?.leftBorderCustomClass;
  const customRightBorderClass = lightingClassConfig?.customClasses?.rightBorderCustomClass;

  return {
    isRoadClassM: !!(roadClasses?.includes("M") || customRoadClass?.class_type === "M"),
    isRoadClassC: !!(roadClasses?.includes("C") || customRoadClass?.class_type === "C"),
    isRoadClassP: !!(roadClasses?.includes("P") || customRoadClass?.class_type === "P"),
    isLeftBorderClassP: !!(leftBorderClass?.includes("P") || customLeftBorderClass?.class_type === "P"),
    isLeftBorderClassC: !!(leftBorderClass?.includes("C") || customLeftBorderClass?.class_type === "C"),
    isRightBorderClassP: !!(rightBorderClass?.includes("P") || customRightBorderClass?.class_type === "P"),
    isRightBorderClassC: !!(rightBorderClass?.includes("C") || customRightBorderClass?.class_type === "C"),
  };
};
